import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import type { ActiveTab } from '../components/workspaceNavigationItems';

export type WorkspaceViewportMode = 'mobile' | 'tablet' | 'desktop';
export type WorkspaceTabletPane = 'list' | 'stage';

export const WORKSPACE_MOBILE_QUERY = '(max-width: 47.99em)';
export const WORKSPACE_DESKTOP_QUERY = '(min-width: 75em)';
export const WORKSPACE_DESKTOP_COLLAPSE_STORAGE_KEY = 'locs.workspace.desktopListCollapsed';

function readDesktopCollapsePreference() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const stored = window.localStorage.getItem(WORKSPACE_DESKTOP_COLLAPSE_STORAGE_KEY);
    return stored === 'true';
  } catch {
    return false;
  }
}

function writeDesktopCollapsePreference(collapsed: boolean) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(WORKSPACE_DESKTOP_COLLAPSE_STORAGE_KEY, String(collapsed));
  } catch {
    // Storage failures should never surface as workspace UI errors.
  }
}

type UseAdaptiveWorkspaceLayoutOptions = {
  activeTab: ActiveTab;
  activeSessionKey: string | null;
};

export function useAdaptiveWorkspaceLayout({
  activeTab,
  activeSessionKey,
}: UseAdaptiveWorkspaceLayoutOptions) {
  const isMobileViewport = useMediaQuery(WORKSPACE_MOBILE_QUERY, false, { getInitialValueInEffect: false });
  const isDesktopViewport = useMediaQuery(WORKSPACE_DESKTOP_QUERY, false, { getInitialValueInEffect: false });
  const [desktopListCollapsed, setDesktopListCollapsed] = useState(readDesktopCollapsePreference);
  const [tabletPane, setTabletPane] = useState<WorkspaceTabletPane>('list');
  const previousViewportModeRef = useRef<WorkspaceViewportMode | null>(null);
  const previousActiveSessionKeyRef = useRef<string | null>(null);

  const viewportMode: WorkspaceViewportMode = isMobileViewport
    ? 'mobile'
    : isDesktopViewport
      ? 'desktop'
      : 'tablet';

  useEffect(() => {
    if (viewportMode !== 'tablet') {
      setTabletPane('list');
    } else if (activeTab === 'plaza') {
      // Hubs is a stage-only destination on tablet; there is no list pane to show.
      setTabletPane('stage');
    } else if (activeTab !== 'chats' || !activeSessionKey) {
      setTabletPane('list');
    } else {
      const enteredTablet = previousViewportModeRef.current !== 'tablet';
      const selectedDifferentSession = previousActiveSessionKeyRef.current !== activeSessionKey;

      if (enteredTablet || selectedDifferentSession) {
        setTabletPane('stage');
      }
    }

    previousViewportModeRef.current = viewportMode;
    previousActiveSessionKeyRef.current = activeSessionKey;
  }, [activeSessionKey, activeTab, viewportMode]);

  const toggleDesktopListCollapsed = useCallback(() => {
    setDesktopListCollapsed((current) => {
      const next = !current;
      writeDesktopCollapsePreference(next);
      return next;
    });
  }, []);

  const showStageOnTablet = useCallback(() => {
    setTabletPane('stage');
  }, []);

  const showListOnTablet = useCallback(() => {
    setTabletPane('list');
  }, []);

  const effectiveListCollapsed = viewportMode === 'desktop' && desktopListCollapsed;
  const isMobileStageVisible = viewportMode === 'mobile' && activeTab === 'chats' && Boolean(activeSessionKey);

  return useMemo(() => ({
    viewportMode,
    tabletPane,
    desktopListCollapsed,
    effectiveListCollapsed,
    isMobileStageVisible,
    toggleDesktopListCollapsed,
    showStageOnTablet,
    showListOnTablet,
  }), [
    desktopListCollapsed,
    effectiveListCollapsed,
    isMobileStageVisible,
    showListOnTablet,
    showStageOnTablet,
    tabletPane,
    toggleDesktopListCollapsed,
    viewportMode,
  ]);
}
